<template>
  <div>
    <workflow-wrapper>
      <orig-court-cases-table v-if="stepID === 'origCourtCasesTable'"
                              key="origCourtCasesTable"
                              @start="start"
                              @edit="edit"
                              @next="$emit('next')"/>
      <edit-orig-court-case v-else-if="stepID === 'edit'"
                            :origCourtCaseID="editOrigCourtCaseID"
                            :generate-judgment-bean="generateJudgmentBean"
                            key="edit"
                            @exit="exitEdit"/>

      <div v-else key="workflow">
        <div class="mx-auto" style="max-width:675px">
          <div class="mb-1" style="font-size:1.4rem">
            {{ $t('page.manageFiling.origCourt.addOrigCourtCase') }}
          </div>

          <v-card class="mb-3 py-2 px-3">
            <breadcrumbs v-model="stepID" :steps="steps"/>
          </v-card>

          <validation-error-panel/>
          <workflow-wrapper>
            <step-case v-if="stepID === 'case'"
                       key="case"
                       :originalBean="bean.case"
                       @save="saveCase"
                       @cancel="cancel"/>
            <step-details v-else-if="stepID === 'details'"
                          key="details"
                          :originalBean="bean.details"
                          @save="saveDetails"
                          @back="gotoCase"
                          @cancel="cancel"/>
            <step-judgments v-else-if="stepID === 'judgments'"
                            key="judgments"
                            :originalBean="bean.judgments"
                            :generate-judgment-bean="generateJudgmentBean"
                            @save="saveJudgments"
                            @back="gotoDetails"
                            @cancel="cancel"/>
            <step-summary v-else-if="stepID === 'summary'"
                          key="summary"
                          :originalBean="bean"
                          @save="saveSummary"
                          @back="gotoJudgments"
                          @cancel="cancel"/>
          </workflow-wrapper>
        </div>
      </div>
    </workflow-wrapper>
  </div>
</template>

<script>
import axios from 'axios';
import WorkflowWrapper from '@components/support/WorkflowWrapper';
import Breadcrumbs from '@components/support/Breadcrumbs';
import ValidationErrorPanel from '@components/support/ValidationErrorPanel';
import { useUserStore } from '@/stores/userStore';
import { useFilingStore } from '@/stores/filingStore';
import OrigCourtCasesTable from '@components/support/manageFiling/originatingCourt/OrigCourtCasesTable';
import EditOrigCourtCase from '@components/support/manageFiling/originatingCourt/EditOrigCourtCase';
import StepCase from '@components/support/manageFiling/originatingCourt/StepCase';
import StepDetails from '@components/support/manageFiling/originatingCourt/StepDetails';
import StepJudgments from '@components/support/manageFiling/originatingCourt/StepJudgments';
import StepSummary from '@components/support/manageFiling/originatingCourt/StepSummary';
import { createOrigCourtCaseBean } from '@/application/filing-core';

const userStore = useUserStore();
const filingStore = useFilingStore();

export default
{
  components: {
    OrigCourtCasesTable, WorkflowWrapper, Breadcrumbs, ValidationErrorPanel, EditOrigCourtCase, StepCase, StepDetails,
    StepJudgments, StepSummary },
  data () {
    return {
      stepID: 'origCourtCasesTable',
      steps: [
        { stepID: 'case', name: this.$t('page.manageFiling.origCourt.step.case') },
        { stepID: 'details', name: this.$t('page.manageFiling.origCourt.step.details') },
        { stepID: 'judgments', name: this.$t('page.manageFiling.origCourt.step.judgments') },
        { stepID: 'summary', name: this.$t('page.manageFiling.origCourt.step.summary') }
      ],
      bean: this.generateFormBean(),
      editOrigCourtCaseID: null
    };
  },
  computed: {
    user () {
      return userStore;
    },
    filingID () {
      return filingStore.filingID;
    }
  },
  watch: {
    stepID (newValue) {
      if (newValue === 'origCourtCasesTable')
      {
        filingStore.toggleSteps({ visible: true, refresh: true });
      }
    }
  },
  methods: {
    createOrigCourtCaseBean,
    start () {
      this.bean = this.generateFormBean();
      this.gotoCase();
      filingStore.toggleSteps({ visible: false });
    },
    edit (origCourtCaseID) {
      this.editOrigCourtCaseID = origCourtCaseID;
      this.stepID = 'edit';
      filingStore.toggleSteps({ visible: false });
    },
    exitEdit () {
      this.stepID = 'origCourtCasesTable';
    },
    gotoCase () {
      this.stepID = 'case';
    },
    saveCase (caseData) {
      this.bean.case = caseData;
      this.gotoDetails();
    },
    gotoDetails () {
      this.stepID = 'details';
    },
    saveDetails (details) {
      this.bean.details = details;
      this.gotoJudgments();
    },
    gotoJudgments () {
      this.stepID = 'judgments';
    },
    saveJudgments (judgments) {
      this.bean.judgments = judgments;
      this.gotoSummary();
    },
    gotoSummary () {
      this.stepID = 'summary';
    },
    saveSummary () {
      this.$cove.blockNow();

      axios.post(this.$cove.getAPI({ name: 'api.filing.originatingCourtCases' }), createOrigCourtCaseBean(this.bean))
        .then(() => {
          this.stepID = 'origCourtCasesTable';
          this.$cove.notify({ message: this.$t('application.save.success'), color: 'success' });
        })
        .finally(() => {
          this.$cove.unblock();
        });
    },
    cancel () {
      this.$cove.confirm({ message: this.$t('global.confirmation.cancel.create') })
        .then((confirm) => {
          if (confirm)
          {
            this.stepID = 'origCourtCasesTable';
          }
        });
    },
    generateFormBean () {
      let bean =
          {
            filingID: this.filingID,
            case: {
              originatingCourt: {
                courtID: null,
                courtName: null
              },
              caseNumber: null,
              caseTitle: null,
              caseType: null,
              county: {
                countyID: null,
                countyName: null
              },
              relatedCaseNumbers: ['']
            },
            details: {
              department: null,
              courtReporter: null,
              dispositionType: null,
              dispositionMethod: null,
              noticeOfAppealDate: null,
              comments: null
            },
            judgments: []
          };
      bean.judgments.push(this.generateJudgmentBean());
      return bean;
    },
    generateJudgmentBean () {
      return {
        judge: null,
        judgmentType: {
          judgmentTypeID: null,
          judgmentTypeValue: null
        },
        judgmentDate: null
      };
    }
  }
};
</script>